import { experiencePath } from '../../services/storefrontExperience';
import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'
import TextField from '@mui/material/TextField'
import MenuItem from '@mui/material/MenuItem'
import Chip from '@mui/material/Chip'
import Paper from '@mui/material/Paper'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import DialogActions from '@mui/material/DialogActions'
import CircularProgress from '@mui/material/CircularProgress'
import Alert from '@mui/material/Alert'
import AddIcon from '@mui/icons-material/Add'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import NotificationSnackbar from '../../components/ui/NotificationSnackbar'
import { useStorefrontCms } from '../../services/fusionBoxCmsService';

const slugify = (value) => String(value || '')
  .trim()
  .toLowerCase()
  .replace(/['"]/g, '')
  .replace(/[^a-z0-9]+/g, '-')
  .replace(/^-+|-+$/g, '')

const protectedRoutes = ['menu', 'single-product', 'fusion-shop', 'create-fusion', 'checkout', 'cart', 'product']
const getFrontendPath = (page) => page.slug === 'home' ? '/' : page.slug === 'single-product' ? '/menu/:product-slug' : `/${page.slug}`

export default function WebsiteIndex() {
  const { fetchCmsPages, createCmsPage, initializeExperience } = useStorefrontCms();
  const navigate = useNavigate()
  const [pages, setPages] = useState([])
  const [loading, setLoading] = useState(true)
  const [initializing, setInitializing] = useState(false)
  const [newPageOpen, setNewPageOpen] = useState(false)
  const [newPage, setNewPage] = useState({ title: '', slug: '', status: 'draft' })
  const [notification, setNotification] = useState({ open: false, message: '', severity: 'info' })

  const notify = (message, severity = 'success') => setNotification({ open: true, message, severity })

  const loadPages = async () => {
    setLoading(true)
    try {
      const rows = await fetchCmsPages()
      setPages(Array.isArray(rows) ? rows : [])
    } catch (err) {
      notify('Failed to load website pages. Check the CMS migration/API.', 'error')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { loadPages() }, [])

  const createPage = async () => {
    if (!newPage.title.trim()) return notify('Page title is required', 'error')
    try {
      const row = await createCmsPage({ ...newPage, slug: newPage.slug || slugify(newPage.title) })
      setNewPageOpen(false)
      setNewPage({ title: '', slug: '', status: 'draft' })
      notify('Page created')
      navigate(experiencePath(`/website/page/${row.id}`))
    } catch (err) {
      notify(err.response?.data?.error || 'Failed to create page', 'error')
    }
  }

  return (
    <Box sx={{ p: 3, background: '#f7f7f6', minHeight: 'calc(100vh - 64px)' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2, mb: 3 }}>
        <Box>
          <Typography variant="overline" sx={{ color: '#ff5f0c', fontWeight: 900, letterSpacing: '.16em' }}>Website Experience</Typography>
          <Typography variant="h5" fontWeight={900} sx={{ color: '#17120D' }}>Website Pages</Typography>
          <Typography variant="body2" sx={{ mt: 0.5, color: '#625C57' }}>
            Pick a page to edit its content. Catalog and Product use this storefront’s catalog; build-box, cart and checkout remain coded separately.
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}><Button variant="outlined" disabled={initializing || loading} onClick={async () => { setInitializing(true); try { await initializeExperience(); await loadPages(); notify('Missing starter pages created as drafts'); } catch (e) { notify(e.response?.data?.error || 'Could not initialize pages', 'error'); } finally { setInitializing(false); } }}>Add starter pages</Button>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setNewPageOpen(true)} sx={{ bgcolor: '#ff5f0c', '&:hover': { bgcolor: '#e45108' } }}>New Page</Button></Box>
      </Box>

      <Alert severity="info" sx={{ mb: 3, border: '1px solid #F0C8A8', bgcolor: '#fff8f1', color: '#5E3A22', '& .MuiAlert-icon': { color: '#ff5f0c' } }}>
        This screen intentionally starts with the pages list. Click any page to edit its sections and content.
      </Alert>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}><CircularProgress /></Box>
      ) : (
        <TableContainer component={Paper} sx={{ borderRadius: 2, border: '1px solid #eadfd6', boxShadow: '0 10px 28px rgba(23,18,13,0.06)' }}>
          <Table sx={{ minWidth: 860 }}>
            <TableHead sx={{ bgcolor: '#fff8f1' }}>
              <TableRow>
                <TableCell>Page</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Type</TableCell>
                <TableCell align="center">Modules</TableCell>
                <TableCell>System</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {!pages.length && (
                <TableRow>
                  <TableCell colSpan={6}>
                    <Alert severity="warning">No pages yet for this storefront. Create a page to get started.</Alert>
                  </TableCell>
                </TableRow>
              )}
              {pages.map(page => (
                <TableRow
                  key={page.id}
                  hover
                  onClick={() => navigate(experiencePath(`/website/page/${page.id}`))}
                  sx={{ cursor: 'pointer', '&:last-child td, &:last-child th': { border: 0 } }}
                >
                  <TableCell>
                    <Typography fontWeight={900}>{page.title}</Typography>
                    <Typography variant="body2" sx={{ color: '#625C57' }}>{getFrontendPath(page)}</Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={page.status}
                      color={page.status === 'published' ? 'success' : 'default'}
                    />
                  </TableCell>
                  <TableCell>
                    <Chip size="small" label={page.page_type || 'custom'} variant="outlined" />
                  </TableCell>
                  <TableCell align="center">
                    <Typography fontWeight={700}>{page.section_count || 0}</Typography>
                  </TableCell>
                  <TableCell>
                    {page.is_locked
                      ? <Chip size="small" label="System" color="warning" />
                      : <Typography variant="body2" color="text.secondary">Custom</Typography>}
                  </TableCell>
                  <TableCell align="right">
                    <Button
                      size="small"
                      variant="outlined"
                      sx={{ borderColor: '#ff5f0c', color: '#ff5f0c' }}
                      endIcon={<ChevronRightIcon />}
                      onClick={(e) => { e.stopPropagation(); navigate(experiencePath(`/website/page/${page.id}`)) }}
                    >
                      Open Builder
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={newPageOpen} onClose={() => setNewPageOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create New Website Page</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '12px !important' }}>
          <TextField label="Page Title" value={newPage.title} onChange={e => setNewPage(p => ({ ...p, title: e.target.value, slug: p.slug || slugify(e.target.value) }))} fullWidth size="small" />
          <TextField label="Slug" value={newPage.slug} onChange={e => setNewPage(p => ({ ...p, slug: slugify(e.target.value) }))} fullWidth size="small" helperText={`Protected slugs: ${protectedRoutes.join(', ')}`} />
          <TextField select label="Status" value={newPage.status} onChange={e => setNewPage(p => ({ ...p, status: e.target.value }))} fullWidth size="small">
            <MenuItem value="draft">Draft</MenuItem>
            <MenuItem value="published">Published</MenuItem>
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNewPageOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={createPage}>Create & Open</Button>
        </DialogActions>
      </Dialog>

      <NotificationSnackbar open={notification.open} message={notification.message} severity={notification.severity} onClose={() => setNotification(n => ({ ...n, open: false }))} />
    </Box>
  )
}
